import React from 'react'
import PartySelectionCard, { fmtMoney } from './PartySelectionCard'

/**
 * Step 1 — Select Party. N-party generalization of Scale's
 * UpdateInvoice/FixedUpdateInvoice/Step1.jsx party-selection cards.
 *
 * A fixed case can now carry any number of billable parties (primary
 * plaintiff/defendant + `case_parties` additions on either side), so this
 * renders one row per BILLABLE party (a party with a payment row) instead of
 * three hardcoded slots, plus an "All parties" row when more than one is
 * eligible. This mirrors two precedents already in the codebase rather than
 * inventing a new pattern:
 *   - HourlyUpdateInvoice/Step1.jsx: the "All parties" card is only OFFERED
 *     when there is more than one candidate to combine ("all 1 parties" is a
 *     question with one answer), and a trailing note explains parties that
 *     cannot be edited here instead of silently dropping them.
 *   - This file's own prior two-party version: PartySelectionCard per party,
 *     disabled + reason + click-through toast when ineligible.
 *
 * Disabled rules (Scale parity, widened from 2 parties to N):
 *   "Update all parties":
 *     - fewer than 2 selectable parties (nothing to combine — card omitted)
 *     - non-identical line items across the selectable parties
 *     - non-identical grand totals across the selectable parties
 *   Any individual party:
 *     - that party's invoice is 'paid' (paid_manually / held / pending are
 *       still editable)
 *     - no payment row at all (shown in a short trailing note instead of a
 *       disabled card, so the case manager isn't left wondering where it
 *       went)
 */
function isSelectableStatus(payment) {
    const s = (payment?.rawStatus || payment?.status || '').toLowerCase()
    // 'held' belongs here. Mediators on manual_invoice_approval (Turner ADR)
    // have every invoice sit held until they release it — that hold IS the
    // window the feature exists to create, and it is when an invoice most needs
    // correcting, because nothing has reached the client yet. Scale groups all
    // three the same way (FixedUpdateInvoice/Step1.jsx isPlaintiffPending).
    return s === 'pending' || s === 'paid_manually' || s === 'held'
}

function isStrictlyPaid(payment) {
    return (payment?.rawStatus || payment?.status || '').toLowerCase() === 'paid'
}

function totalOf(payment) {
    return Number(payment?.grandTotal) || Number(payment?.grand_total) || Number(payment?.amount) || 0
}

function itemsKey(items) {
    if (!Array.isArray(items)) {
        try { items = JSON.parse(items || '[]') } catch (_e) { return '' }
    }
    return JSON.stringify((items || []).map((it) => ({
        d: String(it.description || '').trim().toLowerCase(),
        a: Number(it.amount) || 0,
    })))
}

function statusLabel(p) {
    const s = (p?.rawStatus || p?.status || 'pending').toLowerCase()
    if (s === 'paid_manually') return 'Paid Manually'
    if (s === 'paid') return 'Paid'
    if (s === 'held') return 'Held'
    return 'Pending'
}

/**
 * Can every SELECTABLE party be combined into one "All parties" update?
 * Names the specific party that diverges rather than a generic refusal, so
 * the case manager can go fix that invoice instead of guessing.
 */
function computeAllPartiesGate(selectableParties) {
    if (selectableParties.length < 2) {
        return { offer: false, ok: false, reason: '' }
    }
    const baseline = selectableParties[0]
    const baseItemsKey = itemsKey(baseline.invoice?.items)
    const baseTotal = totalOf(baseline.invoice)

    const divergentItems = selectableParties.find(
        (p) => itemsKey(p.invoice?.items) !== baseItemsKey
    )
    if (divergentItems) {
        return {
            offer: true,
            ok: false,
            reason: `${divergentItems.label} has different line items than ${baseline.label}. All parties must have identical line items (same descriptions and amounts) to update together.`,
        }
    }

    const divergentTotal = selectableParties.find(
        (p) => Math.abs(totalOf(p.invoice) - baseTotal) >= 0.01
    )
    if (divergentTotal) {
        return {
            offer: true,
            ok: false,
            reason: `${divergentTotal.label} has a different grand total (${fmtMoney(totalOf(divergentTotal.invoice))}) than ${baseline.label} (${fmtMoney(baseTotal)}). All parties must have the same grand total to update together.`,
        }
    }

    return { offer: true, ok: true, reason: '' }
}

export default function Step1SelectParty({
    // Every party with a payment row on this case, in display order
    // (primary plaintiff, additional plaintiffs, primary defendant,
    // additional defendants). Each entry: { key, label, party, invoice }.
    parties = [],
    // Parties that exist on the case but have never been invoiced — no
    // payment row to edit. Shown as a short note, not a dead card.
    unavailableParties = [],
    selectedOption,
    setSelectedOption,
    onToast,
}) {
    const selectableParties = parties.filter((p) => isSelectableStatus(p.invoice))
    const anyPaid = parties.some((p) => isStrictlyPaid(p.invoice))
    const allPartiesGate = computeAllPartiesGate(selectableParties)

    const headerDescription = parties.length === 0
        ? 'This case has no invoices to update.'
        : selectableParties.length === 0
            ? 'Every invoice on this case is already paid. No updates can be made.'
            : anyPaid
                ? 'Some invoices are already paid. Select an unpaid invoice to make changes.'
                : 'Choose which invoice to update. Only Pending, Held, or Paid Manually invoices can be changed.'

    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-base font-semibold text-gray-900">Select Party</h3>
                <p className="text-sm text-gray-500 mt-0.5">{headerDescription}</p>
            </div>

            <div className="space-y-3">
                {/* Offered only when there is more than one selectable party to
                    combine — "all 1 parties" is a question with one answer
                    (same gate HourlyUpdateInvoice/Step1.jsx uses). */}
                {allPartiesGate.offer && (
                    <PartySelectionCard
                        label={`Update all ${selectableParties.length} parties`}
                        subtext={allPartiesGate.ok
                            ? `Each party's invoice will be updated to ${fmtMoney(totalOf(selectableParties[0]?.invoice))}, split evenly across all ${selectableParties.length}.`
                            : allPartiesGate.reason}
                        selected={selectedOption === 'all'}
                        disabled={!allPartiesGate.ok}
                        disabledReason={allPartiesGate.reason}
                        onSelect={() => setSelectedOption('all')}
                        onClickWhenDisabled={() => onToast?.({ type: 'warning', message: allPartiesGate.reason })}
                    />
                )}

                {parties.map((p) => {
                    const selectable = isSelectableStatus(p.invoice)
                    const reason = selectable
                        ? ''
                        : isStrictlyPaid(p.invoice)
                            ? 'This invoice is already paid.'
                            : 'Cannot update invoice. Only pending, held, or paid manually invoices can be updated.'
                    return (
                        <PartySelectionCard
                            key={p.key}
                            label={`${p.label}${p.party?.name ? ` — ${p.party.name}` : ''} · ${fmtMoney(totalOf(p.invoice))} (${statusLabel(p.invoice)})`}
                            subtext={`Update ${p.label.toLowerCase()}'s invoice only`}
                            selected={selectedOption === p.key}
                            disabled={!selectable}
                            disabledReason={reason}
                            onSelect={() => setSelectedOption(p.key)}
                            onClickWhenDisabled={() => onToast?.({ type: 'warning', message: reason })}
                        />
                    )
                })}
            </div>

            {/* Why a party is missing entirely. Without this the case manager
                sees fewer rows than parties on the case and cannot tell
                "never invoiced" from "broken". */}
            {unavailableParties.length > 0 && (
                <p className="text-[13px] leading-relaxed text-gray-500">
                    {unavailableParties.map((p) => p.label).join(', ')}{' '}
                    {unavailableParties.length === 1 ? 'has' : 'have'} no invoice on this case and cannot be edited here.
                </p>
            )}
        </div>
    )
}
